import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, profile, isActive, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#', type: 'scroll' },
    { name: 'K-12 Grades', href: '/grades', type: 'route' },
    { name: 'SAT Prep', href: '/sat-prep', type: 'route' },
    { name: 'Programs', href: '#learning-system', type: 'scroll' },
    { name: 'Membership', href: '#membership-tiers', type: 'scroll' },
    { name: 'Free Resources', href: '#free-section', type: 'scroll' },
    { name: 'Success Stories', href: '/success-stories', type: 'route' },
    { name: 'Contact', href: '#contact-form', type: 'scroll' }
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, type: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (type === 'route') {
      navigate(href);
    } else if (href === '#') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.querySelector(href);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  const scrollToMembership = () => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById('membership-tiers');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="w-full px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-emerald-500">
              <img
                src="https://public.readdy.ai/ai/img_res/7ed708d0-0e46-4404-b490-9c89f3f516ab.png"
                alt="Augustine Anyimadu-Ahenkae"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <h1 className={`text-lg font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Math Mastery
              </h1>
              <p className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-white/90'}`}>
                by Augustine Anyimadu-Ahenkae
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href, link.type)}
                className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                  link.name === 'K-12 Grades'
                    ? isScrolled
                      ? 'text-emerald-600 font-semibold border-b-2 border-emerald-500 pb-0.5'
                      : 'text-emerald-300 font-semibold border-b-2 border-emerald-300 pb-0.5'
                    : isScrolled
                    ? 'text-gray-700 hover:text-emerald-600'
                    : 'text-white hover:text-emerald-300'
                }`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Desktop Auth UI */}
          <div className="hidden lg:flex items-center gap-4">
            {!user ? (
              <button
                onClick={scrollToMembership}
                className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                My Account
              </button>
            ) : isActive ? (
              <>
                <div
                  onClick={() => navigate('/dashboard')}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white bg-gradient-to-br from-emerald-600 to-teal-600 cursor-pointer hover:opacity-90 transition-opacity ${
                    isScrolled ? 'shadow-md' : 'shadow-lg'
                  }`}
                >
                  {getInitials(user.email || '')}
                </div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={handleSignOut}
                  className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                    isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'
                  }`}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={scrollToMembership}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Upgrade
                </button>
                <button
                  onClick={handleSignOut}
                  className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                    isScrolled ? 'text-gray-500 hover:text-gray-700' : 'text-white/80 hover:text-white'
                  }`}
                >
                  Sign Out
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-300 cursor-pointer ${
              isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 bg-white rounded-lg shadow-xl">
            <div className="flex flex-col gap-2">
              {navLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  onClick={(e) => handleLinkClick(e, link.href, link.type)}
                  className={`px-6 py-3 text-base font-medium transition-colors duration-300 cursor-pointer ${
                    link.name === 'K-12 Grades'
                      ? 'text-emerald-600 font-semibold bg-emerald-50'
                      : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-600'
                  }`}
                >
                  {link.name}
                </a>
              ))}
              
              {/* Mobile Auth UI */}
              <div className="px-6 pt-2 border-t border-gray-200 mt-2">
                {!user ? (
                  <button
                    onClick={scrollToMembership}
                    className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    My Account
                  </button>
                ) : isActive ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white bg-gradient-to-br from-emerald-600 to-teal-600">
                        {getInitials(user.email || '')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                        <p className="text-xs text-emerald-600 font-semibold">Active Member</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        navigate('/dashboard');
                      }}
                      className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Go to Dashboard
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="w-full px-6 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white bg-gray-400">
                        {getInitials(user.email || '')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                        <p className="text-xs text-gray-500">Inactive</p>
                      </div>
                    </div>
                    <button
                      onClick={scrollToMembership}
                      className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Upgrade Membership
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="w-full px-6 py-3 text-gray-500 font-medium rounded-lg hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}